<template>
<div class="pg-fasttable" @keydown.enter="focusInputInColumn($event, 1, true)" @keydown.down="focusInputInColumn($event, 1, false)" @keydown.up="focusInputInColumn($event, -1, false)">
    <div class="flex-header" v-if="!actionsPortalTo">
        <div class="page-actions" v-if="showSearch || showFilter || showMoreActions">
            <div class="page-actions-search" v-if="showSearch">
                <a v-if="sections.length > 1" href class="collapse-trigger" :class="{'is-expanded': !allSectionsCollapsed}" @click.prevent="toggleCollapse()">
                    <collapse-toggle-button />
                </a>
                <page-search v-model="search" />
                <!--
                <button type="button" class="ml-1 btn btn-outline-primary" :disabled="refreshing" @click="refresh()">
                    <i class="fas fa-sync" :class="{'fa-spin': refreshing}"></i>
                </button>
                -->
            </div>

            <table-filter
                v-if="showFilter"
                :sections="sections"
                :headers="headersWithIds"
                :data="searchedData"
                :filteredData="filteredData"
                @filter="onFilter"
                :managers="managers"
                @filter-edit-open="$emit('filter-edit-open')"
                @filter-edit-close="$emit('filter-edit-close')"
                :use-overlay-filter-editor="useOverlayFilterEditor"
                :encode-filters-to-url="encodeFiltersToUrl"
                ref="tablefilter"
            />

            <div class="page-actions-main">
                <slot name="table-actions"></slot>
                <date-filter-trigger v-if="useDateFilters && sections.length == 1" :date-filter="defaultDateFilter" @click="onDateFilterClick('section-' + sections[0].id)" />
                <more-actions-menu ref="moreActionsMenu" v-if="showMoreActions" />
            </div>
        </div>
    </div>

    <portal :to="actionsPortalTo" v-if="actionsPortalTo">
        <div class="flex-header">
            <div class="page-actions" v-if="showSearch || showFilter || showMoreActions">
                <div class="page-actions-search" v-if="showSearch">
                    <a v-if="sections.length > 1" href class="collapse-trigger" :class="{'is-expanded': !allSectionsCollapsed}" @click.prevent="toggleCollapse()">
                        <collapse-toggle-button />
                    </a>
                    <page-search v-model="search" />
                    <!--
                    <button type="button" class="ml-1 btn btn-outline-primary" :disabled="refreshing" @click="refresh()">
                        <i class="fas fa-sync" :class="{'fa-spin': refreshing}"></i>
                    </button>
                    -->
                </div>

                <table-filter
                    v-if="showFilter"
                    :sections="sections"
                    :headers="headersWithIds"
                    :data="searchedData"
                    :filteredData="filteredData"
                    @filter="onFilter"
                    :managers="managers"
                    @filter-edit-open="$emit('filter-edit-open')"
                    @filter-edit-close="$emit('filter-edit-close')"
                    :use-overlay-filter-editor="useOverlayFilterEditor"
                    :encode-filters-to-url="encodeFiltersToUrl"
                    ref="tablefilter"
                />

                <div class="page-actions-main">
                    <slot name="table-actions"></slot>
                    <date-filter-trigger v-if="useDateFilters && sections.length == 1" :date-filter="defaultDateFilter" @click="onDateFilterClick('section-' + sections[0].id)" />
                    <more-actions-menu ref="moreActionsMenu" v-if="showMoreActions" />
                </div>
            </div>
        </div>
    </portal>

    <div class="flex-body content-main">
        <div class="table-wrapper table-responsive" v-if="rows.length" ref="table">
            <div class="table css-table" tabindex="1" :class="tableClasses" style="height: 100%">

                <!-- Plain keyed v-for instead of a DOM-recycling virtual scroller. Rows mount/unmount
                     by stable :key, so a cell's in-progress input state (and focus) can never bleed
                     onto another row when scrolling — the bug that recycling caused.
                     visibleRows === rows today. If paygrids grow to thousands of rows, swap in
                     NON-recycling windowing here (render a windowed slice of rows + top/bottom spacer
                     divs); the <row> contract below stays unchanged. -->
                <div class="virtual-table-wrapper" ref="tableBody" style="width: 100%; height: 100%; overflow: auto;">
                    <row
                        v-for="item in visibleRows"
                        :key="item.id"
                        :source="item"
                        :activeRowId="`row-${activeRowId}`"
                        :headers="sortedHeaders"
                        :onclick="onClick"
                        :onselect="onSelect"
                        :onRadioSelect="onRadioSelect"
                        :dateFilterClickHandler="onDateFilterClick"
                        :onSort="onSort"
                        :onToggleSection="onToggleSection"
                        :onToggleSelectAll="onToggleSelectAll"
                        :onColumnSort="onColumnSort"
                        :onBlur="onBlur"
                        :sortColumnId="sortColumnId"
                        :sortIsAsc="sortIsAsc"
                        :stickyWidth="stickyWidth"
                        :hasRowSelect="hasRowSelect"
                        :hasRowRadioSelect="hasRowRadioSelect"
                        :searchIsActive="Boolean(search)"
                        :filterIsActive="Boolean(filters.length)"
                        class="virtual-table-item"
                    >
                        <template #section-status="slotProps">
                            <slot name="section-status" v-bind="slotProps" />
                        </template>
                    </row>
                </div>
            </div>
        </div>
    </div>

    <date-filter-modal
        v-if="dateFilterSection"
        :recordLabel="recordLabel"
        :sectionId="dateFilterSection.id"
        :mode="sectionDateFilters['section-' + dateFilterSection.id].mode"
        :start="sectionDateFilters['section-' + dateFilterSection.id].start"
        :end="sectionDateFilters['section-' + dateFilterSection.id].end"
        @change="onDateFilter"
        @close="dateFilterSection = null"
    />
</div>
</template>

<script>
import $ from 'jquery'
// PGFastTable is a fork of FastTable's index that renders every row (no virtual scroller)
// so inline-edit state can't bleed across rows. Row and Cell are SHARED with FastTable
// (the editing/subrow/section-status features there are opt-in/gated); only this index is
// forked. The search/sort/filter infrastructure is imported from FastTable so the two stay
// in sync.
import Row from '../FastTable/Row'
import SortableMixin from '../FastTable/SortableMixin'
import SearchableMixin from '../FastTable/SearchableMixin'
import FilterableMixin from '../FastTable/FilterableMixin.vue'
import {queryParamsToObject} from '../FastTable/filter-utils'
import TableFilter from '../FastTable/Filter'
import MoreActionsMenu from '@/components/MoreActionsMenu'
import DateFilterModal from '@/components/DateFilterModal'
import DateUtils from '../FastTable/date-utils'
import DateFilterTrigger from '../FastTable/DateFilterTrigger'

export default {
    props: {
        'recordLabel': String,
        'sections': Array,
        'tableClasses': String,
        'headers': Array,
        'defaultSortStack': Array,
        'hasFixedSortOrder': {
            type: Boolean, default: false,
        },
        'data': Array,
        'activeRowId': {
            default: null,
        },
        'managers': Object,
        'fixedTableHeight': Number,
        // Pixels at the bottom of the viewport the table must not grow into,
        // e.g. a page footer that always has to stay visible.
        'bottomOffset': {
            type: Number, default: 0,
        },
        'showFilter': {
            type: Boolean, default: true,
        },
        'showSearch': {
            type: Boolean, default: true,
        },
        'showMoreActions': {
            type: Boolean, default: true,
        },
        'useDateFilters': {
            type: Boolean, default: false,
        },
        'hasRowSelect': {
            type: Boolean, default: false,
        },
        'hasRowRadioSelect': {
            type: Boolean, default: false,
        },
        'useOverlayFilterEditor': {
            type: Boolean, default: false,
        },
        'encodeFiltersToUrl': {
            type: Boolean, default: false
        },
        'useFiltersFromUrl': {
            type: Boolean, default: false
        },
        'actionsPortalTo': String
    },
    emits: ['click', 'click_cell', 'select'],
    mixins: [SortableMixin, SearchableMixin, FilterableMixin, ],
    components: {TableFilter, MoreActionsMenu, DateFilterModal, DateFilterTrigger, Row},
    computed: {
        // Rows actually rendered. Identical to `rows` today (we render everything).
        // To add non-recycling windowing later, return only the in-viewport slice here
        // and add spacer divs in the template — the row contract is unchanged.
        visibleRows() {
            return this.rows
        },
        defaultDateFilter() {
            // This is only used if we have two filters
            if (!this.useDateFilters) {return null}
            if (this.sections.length != 1) {return null}

            return this.sectionDateFilters[Object.keys(this.sectionDateFilters)[0]]
        },
        sortedHeaders() {
            const headers = []
            this.headerOrderIds.forEach(id => {
                headers.push(this.headersWithIds.find(h => h.id == id))
            })
            return headers
        },
        headerIdxMapping() {
            const result = {}
            this.headersWithIds.forEach((h, originalIdx) => {
                result[originalIdx] = this.headerOrderIds.indexOf(h.id)
            })

            return result
        },
        extraProps() {
            return {
                activeRowId: `row-${this.activeRowId}`,
                headers: this.sortedHeaders,
                onclick: this.onClick,
                onblur: this.onBlur,
                oninput: this.onInput,
                onselect: this.onSelect,
                onRadioSelect: this.onRadioSelect,
                dateFilterClickHandler: this.onDateFilterClick,
                onSort: this.onSort,
                onToggleSection: this.onToggleSection,
                onToggleSelectAll: this.onToggleSelectAll,
                onColumnSort: this.onColumnSort,
                sortColumnId: this.sortColumnId,
                sortIsAsc: this.sortIsAsc,
                stickyWidth: this.stickyWidth,
                hasRowSelect: this.hasRowSelect,
                hasRowRadioSelect: this.hasRowRadioSelect,
                searchIsActive: Boolean(this.search),
                filterIsActive: Boolean(this.filters.length),
            }
        },
        rows() {
            if (!this.data) {
                return []
            }

            let rows = []
            this.sections.forEach((section, idx) => {
                let isSectionClosed = false
                const sectionId = 'section-' + section.id

                if (this.sections.length > 1) {
                    isSectionClosed = this.closedSections[sectionId]

                    const hasDateFilter = section.hasDateFilter === false ? false : this.useDateFilters
                    rows.push({
                        isHeading: true,
                        title: section.title,
                        count: this.displayData[idx].length,
                        isClosed: isSectionClosed,
                        id: sectionId,
                        objId: section.objId,
                        dateFilter: this.sectionDateFilters[sectionId],
                        useDateFilters: hasDateFilter,
                        sectionStatus: section.status
                    })
                }

                if (!isSectionClosed) {
                    const sectionRows = []
                    this.displayData[idx].forEach(row => {
                        const r = Object.assign({}, row)
                        r.id = 'row-' + row.id
                        r.sectionId = sectionId
                        r.isRow = true
                        r.showSubrowsAnchorCol = section.showSubrowsAnchorCol
                        r.inputsDisabled = section.inputsDisabled

                        const cells = Array.from(r.cells)  // Make a copy mostly to just initialize an array
                        Object.keys(this.headerIdxMapping).forEach(originalIdx => {
                            // This will re-arrange the cells for this row according to our current draggable column order
                            const newIdx = this.headerIdxMapping[originalIdx]
                            cells[newIdx] = r.cells[originalIdx]
                        })
                        r.cells = cells

                        sectionRows.push(r)
                    })

                    if (sectionRows.length) {
                        rows.push({
                            isHeaders: true,
                            id: 'section-headers-' + idx,
                        })
                        rows = rows.concat(this.sortRows(sectionRows))
                    }
                    else {
                        rows.push({
                            isEmpty: true,
                            id: `empty-${idx}`,
                        })
                    }
                }
            })

            return rows
        },
        allDataIds() {
            const result = []
            this.sections.forEach((section, sectionIdx) => {
                const rows = this.data[sectionIdx]
                result[sectionIdx] = rows.map(r => r.id)
            })
            return result
        },
        displayData() {
            const haveFilters = Boolean(this.filters && this.filters.length)
            const haveSearch = Boolean(this.search.trim())

            if (!haveFilters && !haveSearch) {
                // quick path
                return this.data
            }

            const result = []
            this.sections.forEach((section, sectionIdx) => {
                const foundIds = (this.foundDataIds === null) ? new Set(this.allDataIds[sectionIdx]) : new Set(this.foundDataIds[sectionIdx])
                const filteredIds = (this.filteredDataIds === null) ? new Set(this.allDataIds[sectionIdx]) : new Set(this.filteredDataIds[sectionIdx])

                result[sectionIdx] = this.data[sectionIdx].filter(r => filteredIds.has(r.id) && foundIds.has(r.id))
            })

            return result
        },
        allSectionsCollapsed() {
            let sectionsOpen = 0
            Object.keys(this.closedSections).forEach(sId => {
                if (!this.closedSections[sId]) {
                    sectionsOpen++
                }
            })

            return sectionsOpen < 1
        },
    },
    data() {
        const closedSections = {}
        const sectionDateFilters = {}
        this.sections.forEach((section, sectionIdx) => {
            closedSections['section-' + section.id] = section.defaultIsClosed
            const hasDateFilter = section.hasDateFilter === false ? false : this.useDateFilters
            if (hasDateFilter) {
                sectionDateFilters['section-' + section.id] = DateUtils.makeDateRange('last30', null, null)
            }
        })

        const headers = this.makeHeadersWithIds()
        return {
            CONTAINER_PADDING: 16,
            tableHeight: 0,
            closedSections: closedSections,
            stickyWidth: $(this.$refs.table).width() || 0,

            filters: [],
            search: '',
            dateFilterSection: null,
            sectionDateFilters: sectionDateFilters,
            headersWithIds: headers,
            headerOrderIds: headers.map(h => h.id),
            selectedRows: [],
            selectedRow: null,
        }
    },
    mounted() {
        $(window).on('resize', this.onResize)
        this.$bus.$on('resize', this.onResize)
        window.dispatchEvent(new Event('resize'));
    },
    watch: {
        data: {
            handler() {
                const selectedRows = []
                this.rows.forEach(row => {
                    if (row.isRow && row.isSelected) {
                        selectedRows.push(row)
                    }
                })
                this.selectedRows = selectedRows
                this.onResize()
                this.performSearch()
            },
            immediate: true,
        },
        bottomOffset() {
            this.onResize()
        },
        useFiltersFromUrl(newVal, oldVal) {
            if (newVal && !oldVal) {
                this.$nextTick(() => { // nextTick so table data can be populated before we apply a filter
                    this.addFiltersFromUrl()
                })
            }
        }
    },
    methods: {
        addFiltersFromUrl() {
            if (this.$route.query.numFilters) {
                let qsFilters = queryParamsToObject(this.$route.query)
                if (qsFilters.numFilters && qsFilters.filters) {
                    this.$refs.tablefilter.addFilters(qsFilters.filters)
                }
            }
        },
        makeHeadersWithIds() {
            const result = []
            this.headers.forEach(h => {
                const slug = h.label.trim().toLowerCase().replace(/\s/g, '-')
                let id = slug
                for (let i = 1; result.indexOf(id) >= 0; i++) {
                    id = slug + '-' + i
                }

                // if the ID is explicitly specified, use that
                if (h.hasOwnProperty('id')) {
                    id = h.id
                }

                result.push(Object.assign({}, h, {'id': id}))
            })
            return result
        },
        getDefaultSortStack() {
            return this.defaultSortStack || []
        },
        getFixedTableHeight() {
            return this.fixedTableHeight
        },
        onResize() {
            this.$nextTick(() => {
                if (!this.$refs.tableBody) {return}

                // make sure this is before the height adjustment
                this.stickyWidth = $(this.$refs.table).width() - 17 // Magic number to account for vert scrollbar
                if (this.getFixedTableHeight()) {
                    $(this.$refs.table).height(this.getFixedTableHeight())
                    return
                }

                // const tableScrollOffset = this.$refs.tableBody.getOffset()

                const $t = $(this.$refs.table)
                const offset = $t.offset()
                if (!offset) {
                    return
                }

                if ($t.parents('.modal-body').length == 0) {
                    this.tableHeight = Math.max(0, $(window).height() - offset.top - this.CONTAINER_PADDING - this.bottomOffset)
                }
                else {
                    this.tableHeight = $t.parents('.modal-body:first').height() - (
                        $t.offset().top -
                        $t.parents('.modal-body:first').offset().top
                    )
                }

                $t.height(this.tableHeight)

                // this.resetToOffset(tableScrollOffset)
            })
        },
        onClick(msg) {
            if (msg.columnIdx !== null) {
                if (this.headersWithIds[msg.columnIdx].isClickable) {
                    this.$emit('click_cell', {
                        object: msg.row.object,
                        columnIdx: msg.columnIdx,
                    })

                    return
                }
            }

            // if this table has radio buttons, consider a click as a radio select
            if (this.hasRowRadioSelect) {
                this.onRadioSelect(msg.row)
                return
            }

            this.$emit('click', msg.row.object)
        },
        onBlur(msg) {
            this.$emit('blur', msg)
        },
        onInput(msg) {
            this.$emit('input', msg)
        },
        focusInputInColumn(event, step, blurIfNone) {
            // Move focus to the next/previous input in the same column. Scoped to inputs
            // inside a .td so search/filter inputs (which live in .flex-header) are unaffected.
            // step = 1 for next (Enter, Down), -1 for previous (Up).
            // blurIfNone = true when "at the end" should commit the current edit (Enter only).
            const target = event.target
            if (!target || target.tagName !== 'INPUT') return

            const td = target.closest('.td')
            if (!td) return

            event.preventDefault()

            const tr = td.parentElement
            const colIndex = Array.from(tr.children).indexOf(td)
            if (colIndex < 0) return

            const table = target.closest('.css-table')
            if (!table) return

            // .tr matches data rows, subrows, and column-header rows. Column-header rows
            // contain no inputs, so they're naturally skipped by the input lookup below.
            const trs = Array.from(table.querySelectorAll('.tr'))
            const startIdx = trs.indexOf(tr)
            if (startIdx < 0) return

            for (let i = startIdx + step; i >= 0 && i < trs.length; i += step) {
                const candidateTd = trs[i].children[colIndex]
                if (!candidateTd) continue
                const input = candidateTd.querySelector('input')
                // offsetParent === null catches display:none (closed subrows / hidden sections).
                if (input && input.offsetParent !== null) {
                    input.focus()
                    input.select()
                    return
                }
            }

            // No further input in this direction. Enter commits the current edit; arrows
            // leave the user in place rather than blurring out of the grid unexpectedly.
            if (blurIfNone) {
                target.blur()
            }
        },
        onSelect(row) {
            if (this.selectedRows.find(r => r.id == row.id)) {
                this.selectedRows = this.selectedRows.filter(r => r.id != row.id)
            }
            else {
                this.selectedRows.push(row)
            }

            this.$emit('select', this.selectedRows)
        },
        onRadioSelect(row) {
            if (row.selectDisabled) {
                return
            }

            this.selectedRow = row
            this.$emit('click', this.selectedRow.object)
        },
        onToggleSelectAll(sectionId) {
            const section = this.sections[sectionId.replace(/^section-headers-/, '')]
            const rows = this.rows.filter(r => (r.sectionId == 'section-' + section.id) && (!r.selectDisabled))
            const rowIds = rows.map(r => r.id)

            // Either [true, false], [true], or [false]. If [true], then all rows are selected, otherwise not all are selected
            const allSelected = !(new Set(rows.map(r => r.isSelected))).has(false)

            if (allSelected) {
                let selectedRows = Array.from(this.selectedRows)
                selectedRows = selectedRows.filter(r => !rowIds.includes(r.id))
                this.selectedRows = selectedRows
            }
            else {
                let selectedRows = Array.from(this.selectedRows)
                rows.forEach(row => {
                    if (!selectedRows.find(r => r.id == row.id)) {
                        selectedRows.push(row)
                    }
                })
                this.selectedRows = selectedRows
            }

            this.$emit('select', this.selectedRows)
        },
        onToggleSection(sectionId) {
            // const offset = this.$refs.tableBody ? this.$refs.tableBody.getOffset() : 0
            this.closedSections[sectionId] = !this.closedSections[sectionId]
            // this.resetToOffset(offset)
        },
        toggleCollapse() {
            // const offset = this.$refs.tableBody ? this.$refs.tableBody.getOffset() : 0
            let sectionsOpen = 0
            Object.keys(this.closedSections).forEach(sId => {
                if (!this.closedSections[sId]) {
                    sectionsOpen++
                }
            })

            Object.keys(this.closedSections).forEach(sId => {
                this.closedSections[sId] = sectionsOpen ? true : false
            })

            // this.resetToOffset(offset)
        },
        resetToOffset(offset) {
            // tableBody is now a plain scroll container (no VirtualScroller API).
            this.$nextTick(() => {
                if (this.$refs.tableBody) {
                    this.$refs.tableBody.scrollTop = offset || 0
                }
            })
        },
        onFilter(filters) {
            this.filters = filters
        },
        onDateFilterClick(sectionId) {
            this.dateFilterSection = this.sections.find(s => 'section-' + s.id == sectionId)
        },
        onDateFilter(dateFilter) {
            const sectionId = 'section-' + this.dateFilterSection.id
            this.sectionDateFilters[sectionId] = Object.assign({}, dateFilter)

            const eventData = {}
            this.sections.forEach(s => {
                const sectionId = 'section-' + s.id
                eventData[s.id] = this.sectionDateFilters[sectionId]
            })

            this.$emit('date_filter_change', eventData)
        },
        onColumnSort(ids) {
            this.headerOrderIds = ids
        },
    },
}
</script>
